import styled from 'styled-components';
import { useMemo } from 'react';
import { FolderManagerMimeTypes } from '../../../interfaces';
import { SlidesViewFilters } from './FolderManagerSlidesViewFilters';

//region INTERFACES
export interface IOnItemUploadPayload {
  files: FileList;
  item: FolderManagerItem | null;
}

export interface FolderManagerGridViewProps {
  items: FolderManagerItem[];
  selectedRows: FolderManagerItem[];
  noDataMessage?: string;
  onDragEnd: (data: any) => void;
  onDoubleClick: (data: any) => void;
  onSelect: (data: any) => void;
  onGroupHeaderSelect?: (items: FolderManagerItem[]) => void;
  onAction: (data: IOnAction<IActionItemPayload | IOnItemUploadPayload>) => void;
  onDragStart: (data: any) => void;
  isLoading: boolean;

  disableUpload?: boolean;
  minHeight?: string;
  disableInternalDnD?: boolean;
  acceptedDragTypes?: string[];
  viewStateKey?: string;
  slidesViewEnabled?: boolean;
  showThemePicker?: boolean;
  onAddSlide?: (slide: any) => void;
  onSelectionChange?: (items: any[]) => void;
  zoomValue?: number;
  disableSelection?: boolean;
  /** Enables compact grid layout (many small columns, tight gap) and compact item rendering */
  compactGrid?: boolean;
  compactGridMaxColumns?: number;
  selectedKeys?: Set<string>;
  filters?: SlidesViewFilters;
  onFiltersChange?: (filters: SlidesViewFilters) => void;
}

export interface GridViewProps {
  items: FolderManagerItem[];
  selectedRows: FolderManagerItem[];
  noDataMessage?: string;
  overId?: string;
  isLoading: boolean;
  disableUpload?: boolean;
  acceptedDragTypes?: string[];
  viewStateKey?: string;
  slidesViewEnabled?: boolean;
  showThemePicker?: boolean;
  onAction: (data: any) => void;
  onSelect: (data: any) => void;
  onGroupHeaderSelect?: (items: FolderManagerItem[]) => void;
  onDoubleClick: (data: any) => void;
  onAddSlide?: (slide: any) => void;
  onSelectionChange?: (items: any[]) => void;
  zoomValue?: number;
  disableSelection?: boolean;
  /** Enables compact grid layout (many small columns, tight gap) and compact item rendering */
  compactGrid?: boolean;
  compactGridMaxColumns?: number;
  selectedKeys?: Set<string>;
  filters?: SlidesViewFilters;
}

export interface StyledGridViewProps {
  $columnsCount?: number;
  $compactGrid?: boolean;
}

export interface GridViewData {
  folders: FolderManagerItem[];
  workspaces: FolderManagerItem[];
  assets: FolderManagerItem[];
}
//endregion

//region STYLED
export const StyledGridContainer = styled.div`
  position: relative;

  .type-header {
    font-size: ${({ theme }) => theme.fontSize.h5};
    color: ${({ theme }) => theme.palette.gray};
    margin: ${({ theme }) => theme.spacing.default} 0 ${({ theme }) => theme.spacing.small};
  }
`;

export const StyledGridView = styled.div<StyledGridViewProps>`
  padding: 0;
  display: grid;
  grid-template-columns: repeat(${({ $columnsCount = 4 }) => $columnsCount}, minmax(0, 1fr));
  gap: ${({ theme, $compactGrid }) => ($compactGrid ? '8px' : theme.spacing.default)};

  @media (max-width: 700px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

//endregion

//region HOOKS
export const useGridViewData = (items: FolderManagerItem[]): GridViewData => {
  return useMemo(() => {
    return items.reduce(
      (acc: GridViewData, item: FolderManagerItem) => {
        if (item.type === FolderManagerMimeTypes.FOLDER) {
          acc.folders.push(item);
        } else if (item.type === FolderManagerMimeTypes.WORKSPACE) {
          acc.workspaces.push(item);
        } else {
          acc.assets.push(item);
        }
        return acc;
      },
      { folders: [], assets: [], workspaces: [] },
    );
  }, [items]);
};

const getThemesSupported = (item: FolderManagerItem): SlideThemesSupported[] | undefined =>
  item.itemData?.slideData?.ThemesSupported ?? item.itemData?.ThemesSupported;

export const getLayoutInSelectedTheme = (
  item: FolderManagerItem,
  selectedThemeId?: number | null,
): SlideLayoutSupportIndicator | undefined => {
  if (item.type !== FolderManagerMimeTypes.SLIDE) {
    return undefined;
  }

  if (!selectedThemeId) {
    return undefined;
  }

  return getThemesSupported(item)?.find((themeSupport) => themeSupport.ThemeAssetVersionId === selectedThemeId)
    ?.SupportLevel;
};
//endregion
